import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    LogOut, Mail, Phone, MapPin, Briefcase, Calendar, Search, Loader2, Trash2,
    CheckCircle2, Circle, Archive, ArrowLeft, RefreshCw, Inbox,
} from "lucide-react";
import { apiClient, TOKEN_KEY, formatApiError } from "@/lib/api";
import { toast } from "sonner";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AdminTestimonials from "@/pages/AdminTestimonials";

const STATUS_OPTIONS = [
    { key: "all", label: "All" },
    { key: "new", label: "New" },
    { key: "reviewed", label: "Reviewed" },
    { key: "archived", label: "Archived" },
];

function StatusBadge({ status }) {
    const map = {
        new: { label: "New", cls: "bg-gold/15 text-gold border-gold/40", Icon: Circle },
        reviewed: { label: "Reviewed", cls: "bg-navy/10 text-navy border-navy/30", Icon: CheckCircle2 },
        archived: { label: "Archived", cls: "bg-navy/5 text-navy/55 border-navy/15", Icon: Archive },
    };
    const cfg = map[status] || map.new;
    const Icon = cfg.Icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.62rem] uppercase tracking-[0.22em] border ${cfg.cls}`}>
            <Icon className="w-3 h-3" strokeWidth={1.5} /> {cfg.label}
        </span>
    );
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [inquiries, setInquiries] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, reviewed: 0, archived: 0 });
    const [loading, setLoading] = useState(true);
    const [activeStatus, setActiveStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [admin, setAdmin] = useState(null);
    const [view, setView] = useState("inquiries");

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        toast.success("Signed out.");
        navigate("/admin/login");
    };

    const fetchAll = useCallback(async (statusFilter = "all") => {
        setLoading(true);
        try {
            const params = statusFilter !== "all" ? { status_filter: statusFilter } : {};
            const [list, statsRes] = await Promise.all([
                apiClient.get("/inquiries", { params }),
                apiClient.get("/inquiries/stats"),
            ]);
            setInquiries(list.data);
            setStats(statsRes.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                navigate("/admin/login");
            } else {
                toast.error(formatApiError(err.response?.data?.detail) || "Failed to load inquiries.");
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        document.title = "Admin Dashboard · Pacific Key Management";
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) { navigate("/admin/login"); return; }
        apiClient.get("/auth/me")
            .then((res) => setAdmin(res.data))
            .catch(() => { localStorage.removeItem(TOKEN_KEY); navigate("/admin/login"); });
    }, [navigate]);

    useEffect(() => { fetchAll(activeStatus); }, [activeStatus, fetchAll]);

    const updateStatus = async (id, newStatus) => {
        try {
            const { data } = await apiClient.patch(`/inquiries/${id}`, { status: newStatus });
            setInquiries((prev) => activeStatus === "all" ? prev.map((i) => i.id === id ? data : i) : prev.filter((i) => i.id !== id));
            setSelected((cur) => (cur && cur.id === id ? data : cur));
            fetchAll(activeStatus);
            toast.success(`Marked as ${newStatus}.`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Update failed.");
        }
    };

    const deleteInquiry = async (id) => {
        if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
        try {
            await apiClient.delete(`/inquiries/${id}`);
            setSelected(null);
            fetchAll(activeStatus);
            toast.success("Inquiry deleted.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed.");
        }
    };

    const filtered = inquiries.filter((i) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            i.name.toLowerCase().includes(s) ||
            i.email.toLowerCase().includes(s) ||
            i.property_location.toLowerCase().includes(s) ||
            i.service_interest.toLowerCase().includes(s)
        );
    });

    return (
        <div className="min-h-screen bg-pearl" data-testid="admin-dashboard-page">
            {/* Top bar */}
            <header className="bg-navy text-pearl">
                <div className="container-luxe py-5 flex items-center justify-between">
                    <div className="flex items-center gap-5">
                        <Link to="/" className="flex items-center gap-2 text-pearl/70 hover:text-gold transition-colors text-[0.65rem] uppercase tracking-[0.28em]" data-testid="admin-back-site">
                            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Site
                        </Link>
                        <div className="hidden sm:block w-px h-6 bg-pearl/20" />
                        <div>
                            <p className="font-serif text-xl">Pacific Key · Admin</p>
                            <p className="text-[0.6rem] uppercase tracking-[0.32em] text-gold/80">Management Dashboard</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-5">
                        {admin && <span className="hidden sm:inline text-xs text-pearl/65" data-testid="admin-current-email">{admin.email}</span>}
                        <button onClick={logout} className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.28em] text-pearl hover:text-gold transition-colors" data-testid="admin-logout">
                            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="container-luxe py-10 lg:py-14">
                {/* Section switcher */}
                <div className="flex gap-px bg-navy/10 mb-10 w-full sm:w-auto sm:inline-flex" data-testid="admin-view-switcher">
                    <button onClick={() => setView("inquiries")} data-testid="admin-view-inquiries"
                        className={`flex-1 sm:flex-none px-8 py-3 text-[0.7rem] uppercase tracking-[0.28em] transition-colors ${view === "inquiries" ? "bg-navy text-pearl" : "bg-pearl text-navy/60 hover:text-navy"}`}>Inquiries</button>
                    <button onClick={() => setView("testimonials")} data-testid="admin-view-testimonials"
                        className={`flex-1 sm:flex-none px-8 py-3 text-[0.7rem] uppercase tracking-[0.28em] transition-colors ${view === "testimonials" ? "bg-navy text-pearl" : "bg-pearl text-navy/60 hover:text-navy"}`}>Testimonials</button>
                </div>

                {view === "testimonials" ? (
                    <AdminTestimonials />
                ) : (
                  <>
                {/* Stat cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 mb-10" data-testid="admin-stats">
                    {[
                        { label: "Total", value: stats.total },
                        { label: "New", value: stats.new, accent: true },
                        { label: "Reviewed", value: stats.reviewed },
                        { label: "Archived", value: stats.archived },
                    ].map((s) => (
                        <div key={s.label} className="bg-pearl p-6 sm:p-8">
                            <p className="overline text-navy/55">{s.label}</p>
                            <p className={`mt-3 font-serif text-4xl ${s.accent ? "text-gold" : "text-navy"}`}>{s.value}</p>
                        </div>
                    ))}
                </div>

                {/* Tabs & search */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
                    <Tabs value={activeStatus} onValueChange={setActiveStatus} data-testid="admin-status-tabs">
                        <TabsList className="bg-sand/40 rounded-none p-1 h-auto">
                            {STATUS_OPTIONS.map((opt) => (
                                <TabsTrigger
                                    key={opt.key}
                                    value={opt.key}
                                    data-testid={`admin-tab-${opt.key}`}
                                    className="rounded-none text-[0.7rem] uppercase tracking-[0.28em] px-5 py-2.5 data-[state=active]:bg-navy data-[state=active]:text-pearl text-navy/70"
                                >
                                    {opt.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>

                    </Tabs>

                    <div className="flex items-center gap-3">
                        <div className="relative flex-1 lg:w-80">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" strokeWidth={1.5} />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search inquiries"
                                className="w-full bg-pearl border border-navy/15 pl-10 pr-4 py-2.5 text-sm placeholder:text-navy/40 focus:outline-none focus:border-gold transition-colors"
                                data-testid="admin-search"
                            />
                        </div>
                        <button
                            onClick={() => fetchAll(activeStatus)}
                            className="flex items-center gap-2 px-4 py-2.5 border border-navy/15 text-[0.7rem] uppercase tracking-[0.28em] hover:bg-navy hover:text-pearl transition-colors"
                            data-testid="admin-refresh"
                        >
                            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} /> Refresh
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-pearl border border-navy/10" data-testid="admin-inquiries-table">
                    {loading ? (
                        <div className="flex items-center justify-center py-24 text-navy/55 gap-3">
                            <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> Loading inquiries…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 text-navy/55 gap-4">
                            <Inbox className="w-10 h-10" strokeWidth={1} />
                            <p className="text-sm">No inquiries match your filter.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-sand/40">
                                    <tr className="text-left text-[0.62rem] uppercase tracking-[0.22em] text-navy/55">
                                        <th className="px-6 py-4 font-medium">Name</th>
                                        <th className="px-6 py-4 font-medium hidden md:table-cell">Community</th>
                                        <th className="px-6 py-4 font-medium hidden lg:table-cell">Service</th>
                                        <th className="px-6 py-4 font-medium hidden sm:table-cell">Submitted</th>
                                        <th className="px-6 py-4 font-medium">Status</th>
                                        <th className="px-6 py-4 font-medium text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((row) => (
                                        <tr key={row.id} className="border-t border-navy/10 hover:bg-sand/20 transition-colors" data-testid={`inquiry-row-${row.id}`}>
                                            <td className="px-6 py-5">
                                                <p className="font-serif text-lg leading-tight">{row.name}</p>
                                                <p className="text-xs text-navy/55 mt-1">{row.email}</p>
                                            </td>
                                            <td className="px-6 py-5 hidden md:table-cell text-navy/75">{row.property_location}</td>
                                            <td className="px-6 py-5 hidden lg:table-cell text-navy/75">{row.service_interest}</td>
                                            <td className="px-6 py-5 hidden sm:table-cell text-navy/65 text-xs whitespace-nowrap">
                                                {new Date(row.submitted_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                                            </td>
                                            <td className="px-6 py-5"><StatusBadge status={row.status} /></td>
                                            <td className="px-6 py-5 text-right">
                                                <button
                                                    onClick={() => setSelected(row)}
                                                    data-testid={`inquiry-view-${row.id}`}
                                                    className="text-[0.65rem] uppercase tracking-[0.28em] underline underline-offset-4 hover:text-gold transition-colors"
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                  </>
                )}
            </main>

            {/* Detail dialog */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl rounded-none border-navy/15 bg-pearl p-0" data-testid="inquiry-detail-dialog">
                    {selected && (
                        <div>
                            <DialogHeader className="p-8 border-b border-navy/10">
                                <p className="overline text-gold">Inquiry</p>
                                <DialogTitle className="font-serif text-3xl text-navy mt-2">{selected.name}</DialogTitle>
                                <DialogDescription className="sr-only">Inquiry details and status management</DialogDescription>
                                <div className="mt-3"><StatusBadge status={selected.status} /></div>
                            </DialogHeader>

                            <div className="p-8 space-y-6">
                                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                                    <Detail icon={Mail} label="Email" value={selected.email} href={`mailto:${selected.email}`} />
                                    <Detail icon={Phone} label="Phone" value={selected.phone} href={`tel:${selected.phone}`} />
                                    <Detail icon={MapPin} label="Property Location" value={selected.property_location} />
                                    <Detail icon={Briefcase} label="Service Interest" value={selected.service_interest} />
                                    <Detail icon={Calendar} label="Submitted" value={new Date(selected.submitted_at).toLocaleString()} />
                                </div>

                                <div>
                                    <p className="overline text-navy/55 mb-2">Message</p>
                                    <p className="text-sm text-navy/85 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                                </div>
                            </div>

                            <div className="p-6 border-t border-navy/10 flex flex-wrap items-center justify-between gap-3 bg-sand/30">
                                <div className="flex flex-wrap gap-2">
                                    <button onClick={() => updateStatus(selected.id, "new")} disabled={selected.status === "new"} className="px-4 py-2 text-[0.65rem] uppercase tracking-[0.28em] border border-navy/20 hover:bg-navy hover:text-pearl disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="inquiry-mark-new">Mark New</button>
                                    <button onClick={() => updateStatus(selected.id, "reviewed")} disabled={selected.status === "reviewed"} className="px-4 py-2 text-[0.65rem] uppercase tracking-[0.28em] border border-navy/20 hover:bg-navy hover:text-pearl disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="inquiry-mark-reviewed">Mark Reviewed</button>
                                    <button onClick={() => updateStatus(selected.id, "archived")} disabled={selected.status === "archived"} className="px-4 py-2 text-[0.65rem] uppercase tracking-[0.28em] border border-navy/20 hover:bg-navy hover:text-pearl disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="inquiry-mark-archived">Archive</button>
                                </div>
                                <button onClick={() => deleteInquiry(selected.id)} className="flex items-center gap-2 px-4 py-2 text-[0.65rem] uppercase tracking-[0.28em] text-red-700 border border-red-200 hover:bg-red-50 transition-colors" data-testid="inquiry-delete">
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
                                </button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Detail({ icon: Icon, label, value, href }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="w-4 h-4 text-gold mt-1" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
                <p className="overline text-navy/55 mb-0.5">{label}</p>
                {href ? (
                    <a href={href} className="text-sm text-navy hover:text-gold transition-colors break-all">{value}</a>
                ) : (
                    <p className="text-sm text-navy break-words">{value}</p>
                )}
            </div>
        </div>
    );
}
